import { Link } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.living';
const articleUrl = `${siteUrl}/artigo/hoobuy-spreadsheet-guia`;

export default function ArticlePage() {
  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      <article>
        <header className="relative w-full pt-24 pb-16 md:pt-32 md:pb-20 bg-foreground-950">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <nav className="flex items-center gap-2 text-xs md:text-sm mb-6" aria-label="Breadcrumb">
              <Link to="/" className="text-white/50 hover:text-white/80 cursor-pointer transition-colors">
                Início
              </Link>
              <span className="text-white/30">/</span>
              <span className="text-white/80">Guia Completo Hoobuy Spreadsheet</span>
            </nav>

            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500/20 text-primary-300 mb-4">
              SEO Guide
            </span>
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
              O que é Hoobuy Spreadsheet? Guia Completo 2026
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/60 max-w-2xl leading-relaxed">
              Descubra tudo sobre a Hoobuy Spreadsheet: como usar, melhores produtos, dicas de compra, segurança e muito mais. Guia definitivo em português atualizado para 2026.
            </p>

            <div className="mt-6 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <span className="text-white/80 font-semibold text-sm">HS</span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">Equipe Hoobuy Sheet</p>
                <p className="text-xs text-white/40">Atualizado em Junho 2026 · 8 min de leitura</p>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Neste Artigo</h4>
                <nav className="space-y-2">
                  {[
                    'O que é Hoobuy Spreadsheet',
                    'Por que usar uma planilha',
                    'Como funciona',
                    'Categorias disponíveis',
                    'Hoobuy é seguro',
                    'Comparação com outros',
                    'Como começar',
                    'Conclusão',
                  ].map((item, i) => (
                    <a
                      key={i}
                      href={`#section-${i + 1}`}
                      className="block text-sm text-foreground-500 hover:text-primary-600 cursor-pointer transition-colors py-1"
                    >
                      {item}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="prose-custom space-y-10">
                <section id="section-1">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    O que é Hoobuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    A <strong>Hoobuy Spreadsheet</strong>, também conhecida como <strong>planilha Hoobuy</strong>, é uma coleção organizada e curada de links de produtos disponíveis na plataforma Hoobuy. Esta ferramenta se tornou essencial para compradores que desejam encontrar os melhores produtos de forma rápida e eficiente, sem perder horas navegando manualmente pela plataforma.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    A planilha reúne mais de <strong>10.000 produtos selecionados</strong>, organizados por categorias como tênis, roupas, hoodies, jaquetas, acessórios e muito mais. Cada link é verificado regularmente pela nossa equipe para garantir que você sempre tenha acesso aos produtos mais atualizados e com a melhor qualidade disponível.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    Diferente de outras planilhas que você encontra pela internet, a nossa <strong>Hoobuy Spreadsheet em português</strong> foi desenvolvida especificamente para o público brasileiro e português, com descrições claras, categorização intuitiva e links que funcionam. É a ferramenta definitiva para quem quer comprar na Hoobuy sem complicação.
                  </p>
                </section>

                <section id="section-2">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Por que usar uma Hoobuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    Se você já tentou navegar pela plataforma Hoobuy sem uma planilha organizada, sabe como pode ser demorado e frustrante. A <strong>planilha Hoobuy</strong> resolve esse problema reunindo todos os melhores produtos em um único lugar, economizando horas de pesquisa e garantindo que você encontre exatamente o que procura.
                  </p>
                  <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/60 my-6">
                    <h3 className="text-base font-semibold text-foreground-950 mb-3">Vantagens da Hoobuy Spreadsheet:</h3>
                    <ul className="space-y-2">
                      {[
                        'Links verificados e atualizados diariamente pela nossa equipe',
                        'Mais de 10.000 produtos organizados em 14 categorias diferentes',
                        'Produtos selecionados dos melhores vendedores da plataforma',
                        'Avaliações reais de compradores que já testaram os produtos',
                        'Interface simples e intuitiva, mesmo para iniciantes',
                        'Compatível com envio para Brasil e Portugal',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                          <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section id="section-3">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Como funciona a Hoobuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    O funcionamento da <strong>Hoobuy Spreadsheet</strong> é extremamente simples e direto. Ao acessar nossa planilha, você encontrará todos os produtos organizados por categorias. Cada produto possui um link direto que, quando copiado e colado na plataforma Hoobuy, leva você diretamente à página de compra daquele item específico.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    O processo de compra usando a planilha segue cinco etapas simples: escolha o produto na planilha, copie o link, cole na plataforma Hoobuy, adicione ao carrinho e finalize o pagamento. Em poucos minutos você conclui sua compra com total segurança e praticidade.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    Uma das grandes vantagens é que nossa <strong>melhor Hoobuy Spreadsheet</strong> é constantemente atualizada. Links quebrados são removidos, novos produtos populares são adicionados e as categorias são reorganizadas para facilitar ainda mais sua navegação. Isso significa que você sempre terá acesso ao melhor conteúdo disponível na plataforma.
                  </p>
                </section>

                <section id="section-4">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Categorias disponíveis na planilha
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    Nossa <strong>Hoobuy Spreadsheet</strong> conta com 14 categorias cuidadosamente organizadas para cobrir todos os tipos de produtos que você pode encontrar na plataforma. De tênis exclusivos a acessórios de moda, passando por roupas casuais e streetwear, temos tudo que você precisa em um só lugar.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 my-6">
                    {[
                      'Shoes (Tênis)', 'Hoodies/Sweaters', 'T-Shirts (Camisetas)',
                      'Jackets (Jaquetas)', 'Pants/Shorts', 'Headwear (Chapéus/Bonés)',
                      'Sets (Conjuntos)', 'Underwear', 'Jersey (Camisas Esportivas)',
                      'Accessories', 'Bags (Bolsas)', 'Belts (Cintos)',
                      'Perfumes', 'Other Stuff (Outros)',
                    ].map((cat) => (
                      <div key={cat} className="px-3 py-2 rounded-md bg-background-100 border border-background-200/60 text-xs md:text-sm text-foreground-700 text-center">
                        {cat}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    Cada categoria contém dezenas ou centenas de produtos verificados, com links diretos para a plataforma Hoobuy. A organização por categoria permite que você encontre exatamente o tipo de produto que procura em segundos, sem precisar navegar por páginas e páginas de resultados.
                  </p>
                </section>

                <section id="section-5">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Hoobuy é seguro? Tudo que você precisa saber
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    Esta é uma das perguntas mais frequentes entre novos usuários. A resposta é sim: a <strong>Hoobuy é completamente segura</strong> para realizar suas compras. A plataforma oferece diversos mecanismos de proteção que garantem uma experiência de compra tranquila e confiável.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    A plataforma conta com <strong>inspeção de produtos</strong> antes do envio, onde você recebe fotos reais do item que comprou para aprovar antes que ele seja despachado. Além disso, existe <strong>proteção ao comprador</strong> que cobre casos de produtos danificados, extraviados ou diferentes do anunciado.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    As opções de pagamento também são seguras, incluindo métodos como PayPal e cartão de crédito, que oferecem camadas adicionais de proteção. O suporte ao cliente está disponível 24 horas por dia para ajudar com qualquer dúvida ou problema que possa surgir durante o processo de compra.
                  </p>
                </section>

                <section id="section-6">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Hoobuy vs Outros Agentes: Comparação Completa
                  </h2>
                  <div className="overflow-x-auto my-6">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-200/60">
                          <th className="text-left py-3 px-4 font-semibold text-foreground-950">Recurso</th>
                          <th className="text-left py-3 px-4 font-semibold text-primary-600">Hoobuy</th>
                          <th className="text-left py-3 px-4 font-semibold text-foreground-500">Outros Agentes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['Produtos Disponíveis', '10.000+', 'Menos'],
                          ['Inspeção de Produtos', 'Sim (com fotos)', 'Limitada'],
                          ['Segurança', 'Alta', 'Média'],
                          ['Frete', 'Rápido', 'Variável'],
                          ['Suporte ao Cliente', '24 horas', 'Parcial'],
                          ['Planilha em Português', 'Sim (nossa)', 'Raramente'],
                        ].map((row, i) => (
                          <tr key={i} className="border-b border-background-100">
                            <td className="py-3 px-4 text-foreground-700 font-medium">{row[0]}</td>
                            <td className="py-3 px-4 text-primary-600 font-semibold">{row[1]}</td>
                            <td className="py-3 px-4 text-foreground-500">{row[2]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    Como você pode ver na tabela acima, a Hoobuy se destaca em praticamente todos os aspectos quando comparada a outros agentes chineses. A disponibilidade da nossa <strong>Hoobuy Spreadsheet em português</strong> é um diferencial ainda maior, já que facilita a vida de compradores brasileiros e portugueses que não dominam o inglês ou chinês.
                  </p>
                </section>

                <section id="section-7">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Como começar a usar a Hoobuy Spreadsheet hoje
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    Começar a usar a <strong>melhor Hoobuy Spreadsheet</strong> é muito simples e não requer nenhum conhecimento técnico. Basta seguir os passos abaixo para ter acesso imediato a todos os produtos selecionados:
                  </p>
                  <div className="space-y-4 my-6">
                    {[
                      { step: '1', title: 'Acesse a planilha', desc: 'Visite nossa página principal e explore as categorias disponíveis na Hoobuy Spreadsheet.' },
                      { step: '2', title: 'Escolha uma categoria', desc: 'Selecione a categoria de produtos que mais lhe interessa: tênis, roupas, acessórios, etc.' },
                      { step: '3', title: 'Encontre o produto', desc: 'Navegue pelos produtos disponíveis e escolha aquele que deseja comprar.' },
                      { step: '4', title: 'Copie o link', desc: 'Copie o link do produto e cole na plataforma Hoobuy para adicionar ao carrinho.' },
                      { step: '5', title: 'Finalize a compra', desc: 'Escolha o método de pagamento, revise seu pedido e aguarde a entrega na sua casa.' },
                    ].map((item) => (
                      <div key={item.step} className="flex gap-4 items-start">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-bold text-primary-600">{item.step}</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-950">{item.title}</h4>
                          <p className="text-sm text-foreground-600">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section id="section-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Conclusão: Por que escolher a nossa Hoobuy Spreadsheet
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    A <strong>Hoobuy Spreadsheet</strong> é, sem dúvida, a ferramenta mais eficiente para quem deseja comprar na plataforma Hoobuy de forma rápida, segura e organizada. Com mais de 10.000 produtos verificados, 14 categorias e links atualizados diariamente, nossa planilha oferece tudo que você precisa para encontrar os melhores produtos sem perder tempo.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                    Seja você um comprador iniciante ou experiente, a <strong>planilha Hoobuy em português</strong> foi desenvolvida para atender às suas necessidades. Navegação intuitiva, produtos de qualidade e suporte completo fazem da nossa planilha a escolha número um para compradores no Brasil e em Portugal.
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    Não perca mais tempo procurando produtos manualmente. Comece agora mesmo a usar a <strong>melhor Hoobuy Spreadsheet</strong> disponível em português e descubra uma nova forma de comprar na Hoobuy. Com nossa planilha, você economiza tempo, reduz riscos e encontra exatamente o que procura.
                  </p>
                </section>

                <div className="mt-10 p-6 md:p-8 rounded-lg bg-primary-50 border border-primary-100/60 text-center">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-3">
                    Pronto para começar?
                  </h3>
                  <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
                    Acesse agora a Hoobuy Spreadsheet e descubra os melhores produtos selecionados para você.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href={mainSiteUrl}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap transition-all"
                    >
                      Explorar Hoobuy Sheet
                    </a>
                    <Link
                      to="/"
                      className="px-6 py-3 rounded-full border border-primary-200 text-primary-600 text-sm font-medium hover:bg-primary-100 cursor-pointer whitespace-nowrap transition-all"
                    >
                      &larr; Voltar ao Início
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'headline': 'O que é Hoobuy Spreadsheet? Guia Completo 2026',
            'description': 'Descubra tudo sobre a Hoobuy Spreadsheet: como usar, melhores produtos, dicas de compra, segurança e muito mais. Guia definitivo em português atualizado para 2026.',
            'author': {
              '@type': 'Person',
              'name': 'Equipe Hoobuy Sheet',
            },
            'publisher': {
              '@type': 'Organization',
              'name': 'Hoobuy Sheet',
              'url': siteUrl,
            },
            'datePublished': '2026-06-01',
            'dateModified': '2026-06-26',
            'url': articleUrl,
            'inLanguage': 'pt-BR',
            'isAccessibleForFree': true,
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': articleUrl,
            },
          }),
        }}
      />
      <script type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': [
              {
                '@type': 'ListItem',
                'position': 1,
                'name': 'Início',
                'item': siteUrl,
              },
              {
                '@type': 'ListItem',
                'position': 2,
                'name': 'Guia Completo Hoobuy Spreadsheet',
                'item': articleUrl,
              },
            ],
          }),
        }}
      />

      <Footer />
    </main>
  );
}